<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAppStore } from '@/stores/AppStore'
import { useClinicRepository } from '@/repositories/clinicRepo'
import { MembercardWebapp } from '@/type/MemberCard'
import { parse } from 'date-fns'
import { showNotify, showLoadingToast, closeToast } from 'vant'
import ProfileCard from './components/ProfileCard.vue'
// import PointProgressBar from './components/PointProgressBar.vue'
import XpressMenuButton from './components/XpressMenuButton.vue'
import { useXpressProfileMenu } from '@/composables/Layout/useXpressProfileMenu'
import { formatLocalExpireDate } from '@/global'
// import { getMemberCardSpendingSummary } from '@/composables/Patient/usePatientMemberCard'

const router = useRouter()
const appStore = useAppStore()
const clinicRepo = useClinicRepository(appStore.clinicUUID)
const menuItems = useXpressProfileMenu().getMenuItems()

// Member card data
const memberCard = ref<MembercardWebapp | null>(null)

// Computed properties for profile card data
const userPoints = computed(() => Number(memberCard.value?.point) || 0)
// const spendingSummary = computed(() => {
//   return getMemberCardSpendingSummary(memberCard.value)
// })

// const currentSpending = computed(() => {
//   return spendingSummary.value.totalSpending
// })

// const totalPointsRequired = computed(() => {
//   return spendingSummary.value.nextLevelMinSpending
// })

// const totalPointsLeft = computed(() => {
//   return spendingSummary.value.totalSpendingLeft
// })

const expiryDate = computed(() => {
  if (memberCard.value?.point_expire_date) {
    const date = parse(memberCard.value.point_expire_date, 'yyyy-MM-dd', new Date())
    if (isNaN(date.getTime())) {
      console.error('Invalid expire date format: ', date)
      return '-'
    }

    return formatLocalExpireDate(date)
  }
  return '-'
})

// Initialize patient data
const initPatient = async () => {
  if (appStore.patient) {
    return
  }

  showLoadingToast({
    message: 'กำลังโหลดข้อมูลผู้ใช้...',
    forbidClick: true,
    duration: 0,
  })

  try {
    const patientData = await clinicRepo.fetchPatientMiniInfo()
    appStore.setPatient(patientData)
    closeToast()
  } catch (error) {
    console.error('Error fetching patient:', error)
    closeToast()
    showNotify({
      type: 'danger',
      message: 'ไม่สามารถโหลดข้อมูลผู้ใช้ได้ กรุณาลองใหม่อีกครั้ง',
      duration: 5000,
    })
  }
}

// Fetch member card data
const fetchMemberCard = async () => {
  // Check if member card already exists in store
  if (appStore.memberCard) {
    memberCard.value = appStore.memberCard
    return
  }

  // Fetch member card using patient uuid
  if (appStore.patient?.uuid) {
    showLoadingToast({
      message: 'กำลังโหลดข้อมูลสมาชิก...',
      forbidClick: true,
      duration: 0,
    })

    try {
      await appStore.setMemberCard()
      memberCard.value = appStore.memberCard
      closeToast()
    } catch (error) {
      closeToast()
      showNotify({
        type: 'danger',
        message: 'ไม่สามารถโหลดข้อมูลสมาชิกได้ กรุณาลองใหม่อีกครั้ง',
        duration: 5000,
      })
    }
  }
}

// Navigation functions
const goToProfileSettings = () => {
  router.push({ name: 'XpressProfileSettings' })
}

const goToPointsDetail = () => {
  router.push({ name: 'XpressProfileAccum' })
}

const goToMenu = (routeName: string) => {
  router.push({ name: routeName })
}

// Initialize data on component mount
onMounted(async () => {
  try {
    await initPatient()
    await fetchMemberCard()
  } catch (error) {
    console.error('Error initializing profile:', error)
    showNotify({
      type: 'danger',
      message: 'เกิดข้อผิดพลาดในการโหลดข้อมูล',
      duration: 5000,
    })
  }
})
</script>

<template>
  <div class="bg-primary2 tw-pt-3">
    <!-- Profile Card -->
    <div class="tw-mx-4">
      <ProfileCard
        :userPoints="userPoints"
        :expireDate="expiryDate"
        @onClickProfile="goToProfileSettings"
        @onClickPoint="goToPointsDetail"
      />
    </div>

    <!-- Progress Section -->
    <!-- <PointProgressBar
      class="tw-mx-3"
      :currentSpending="currentSpending"
      :totalSpendingRequired="totalPointsRequired"
      :totalSpendingLeft="totalPointsLeft"
    /> -->

    <!-- Menu Section: บัตรกำนัล / คอร์สของฉัน -->
    <div
      class="tw-sticky tw-top-0 tw-px-4 tw-pb-4 tw-pt-4 tw-mt-4 tw-bg-white tw-rounded-t-[30px] tw-flex tw-flex-col"
    >
      <XpressMenuButton
        v-for="item in menuItems"
        :key="item.to"
        :label="item.label"
        :icon="item.icon"
        :icon-size-class="item.iconSizeClass"
        @click="goToMenu(item.to)"
      />
    </div>
  </div>
</template>

<style scoped>
/* Custom styles for smooth transitions */
.line-clamp-1 {
  display: -webkit-box;
  line-clamp: 1;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Ensure the page takes full height */
:deep(.van-tabbar) {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
}
</style>
